import type { Maybe } from '@adornis/base/utilTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { AbstractAspect } from '@adornis/filter/aspect-schemas/AbstractAspect.js';
import { DropdownSelectionInput } from '@adornis/formfield/fields/dropdown-selection-input.js';
import { stateful } from '@adornis/functional-lit/stateful.js';
import { html, nothing } from 'lit';
import type { AspectConfig } from '@adornis/filter/filter-options.js';
import { getMandatePhaseSelector, MandatePhase } from './MandatStatus.js';

@Entity()
export class SepaActiveAspect extends AbstractAspect {
  static override _class = 'SepaActiveAspect';

  @Field(() => Boolean)
  value: Maybe<boolean>;

  constructor(doc: PartialEntityData<SepaActiveAspect>) {
    super(doc);
  }

  /**
   * The person view has no `sepaMandats.active` - it depends on the current day and is deliberately
   * not materialized - so this filters the stored mandates instead, staying in sync with what the
   * `active` field resolves to (phase RUNNING). `$elemMatch`, because the whole phase has to hold
   * for the *same* mandate: an expired mandate and a future one must not add up to an active person.
   */
  override getMongoFilter(): object | null {
    if (this.value === null || this.value === undefined) return null;

    const hasActiveMandate = {
      sepaMandats: { $elemMatch: getMandatePhaseSelector([MandatePhase.RUNNING]) },
    };

    return this.value ? hasActiveMandate : { $nor: [hasActiveMandate] };
  }
}

export const SepaActiveAspectSchema: AspectConfig<SepaActiveAspect> = {
  aspectConstructor: SepaActiveAspect,
  renderEditor: stateful(({ aspectValue }) => {
    return html`
      ${DropdownSelectionInput({
        placeholder: 'Ja/Nein',
        selectables: [true, false],
        value: aspectValue.value,
        clearable: true,
        onPicked: value => (aspectValue.value = value),
        renderString: (bool: Maybe<boolean>) => (typeof bool === 'boolean' ? (bool ? 'Ja' : 'Nein') : 'leer'),
      })}
    `;
  }),
  renderPreview: stateful(({ aspectValue }) => {
    return typeof aspectValue.value !== 'boolean'
      ? nothing
      : html`
          <span class="text-sm">
            + ${typeof aspectValue.value === 'boolean' ? (aspectValue.value ? 'Ja' : 'Nein') : 'leer'}
          </span>
        `;
  }),
};
